import React, { useMemo } from 'react'

import { SxProps } from '@mui/material'
import { TGroup, MemberStatus } from 'types/GlobalGroup'

import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useGroups } from 'redux/hooks/Groups'
import { formatGroupOption, userIsPending } from 'utils/functions'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Collapse from 'components_new/atoms/Collapse'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import List from 'components_new/atoms/List'
import Skeleton from 'components_new/atoms/Skeleton'
import Tooltip from 'components_new/atoms/Tooltip'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import GenericListItem from 'components_new/molecules/GenericListItem'
import SectionHeader from 'components_new/molecules/SectionHeader'
import SelectSearch from 'components_new/molecules/SelectSearch'

interface GroupListProps {
  editMode?: boolean
  groups: TGroup[]
  onAdd?: (groupId: string) => void
  onRemove?: (groupId: string) => void
  seperateMyGroups?: boolean
  sx?: SxProps
}

const GroupList = (props: GroupListProps) => {
  const { editMode, groups, onAdd, onRemove, seperateMyGroups, sx } = props

  const { t } = useTranslation()
  const { account, loading: accountLoading } = useAccountSettings()
  const { allGroups, myGroups, loading: groupsLoading } = useGroups()

  const notAddedGroupOptions = useMemo(
    () =>
      allGroups
        .filter((group) => !groups.some((g) => g.id === group.id))
        .map((group) => formatGroupOption(group, t)),
    [allGroups, groups]
  )

  const loading = accountLoading || groupsLoading

  if (loading)
    return (
      <>
        {[1, 2, 3].map((i) => (
          <Skeleton height={40} key={i} sx={{ mb: 1 }} />
        ))}
      </>
    )

  const myGroupIds = new Set(myGroups.map((g) => g.id))
  const inMyGroups = seperateMyGroups
    ? groups.filter((g) => myGroupIds.has(g.id))
    : []
  const otherGroups = seperateMyGroups
    ? groups.filter((g) => !myGroupIds.has(g.id))
    : groups

  const renderGroupItem = (group: TGroup) => {
    const activeMembers = group.members.filter(
      (member) => member.status === MemberStatus.APPROVED
    )
    const isPending = userIsPending(group.members, account?.id as string)

    return (
      <GenericListItem
        action={!editMode ? 'to' : undefined}
        actions={
          editMode && onRemove ? (
            <Box>
              <Tooltip title={t('global', 'delete')}>
                <IconButton onClick={() => onRemove(group.id)} size="small">
                  <Icon fontSize="small" name="RemoveCircleOutlineOutlined" />
                </IconButton>
              </Tooltip>
            </Box>
          ) : undefined
        }
        body={`${activeMembers.length} ${t('groups', 'members', true)}`}
        chip={
          isPending
            ? {
                label: t('global', 'request_sent'),
                size: 'xsmall',
                sx: { ml: 1 }
              }
            : undefined
        }
        color={group.profile_color}
        hasHover={true}
        key={group.id}
        size="medium"
        title={group.name}
        titleIcon={
          group.is_private
            ? {
                name: 'Lock'
              }
            : undefined
        }
        to={!editMode ? `/groups/${group.id}` : undefined}
        variant="group"
      />
    )
  }

  return (
    <Box sx={sx}>
      {onAdd ? (
        <Collapse in={editMode} unmountOnExit={true}>
          <SelectSearch
            closeOnSelect={true}
            color="primary"
            disableClearable={true}
            label={t('global', 'add') + ' ' + t('groups', 'group', true)}
            onChange={(option) => onAdd(option.id)}
            multiple={false}
            options={notAddedGroupOptions}
            selected={null}
            size={'medium'}
            sort={true}
            sx={{ mb: 2 }}
          />
        </Collapse>
      ) : null}

      {seperateMyGroups ? (
        <Box sx={sx}>
          <SectionHeader
            divider={true}
            gutterBottom={true}
            size="large"
            title={t('groups', 'my_groups_title')}
          />
          {inMyGroups.length === 0 ? (
            <EmptyPlaceholder
              background={true}
              title={t('groups', 'no_groups_title')}
            />
          ) : (
            <List dense={true} sx={{ mb: 2 }}>
              {inMyGroups.map(renderGroupItem)}
            </List>
          )}
          <SectionHeader
            divider={true}
            gutterBottom={true}
            size="large"
            title={t('groups', 'other_groups_title')}
          />
          <List dense={true}>{otherGroups.map(renderGroupItem)}</List>
        </Box>
      ) : null}

      {!seperateMyGroups ? (
        <List dense={true} sx={sx}>
          {groups.map(renderGroupItem)}
        </List>
      ) : null}
    </Box>
  )
}

export default GroupList
